/* eslint-disable @typescript-eslint/naming-convention, jsdoc/require-param, jsdoc/require-returns */
import { findChildByName, findDocumentNode, getPropertyValue } from "../types/fbxTypes";
export function extractPropertyTemplates(doc) {
    const templates = new Map();
    const definitions = findDocumentNode(doc, "Definitions");
    if (!definitions) {
        return templates;
    }
    for (const objectTypeNode of definitions.children) {
        if (objectTypeNode.name !== "ObjectType") {
            continue;
        }
        const objectType = getPropertyValue(objectTypeNode, 0);
        if (!objectType) {
            continue;
        }
        for (const templateNode of objectTypeNode.children) {
            if (templateNode.name !== "PropertyTemplate") {
                continue;
            }
            const templateName = getPropertyValue(templateNode, 0);
            if (!templateName) {
                continue;
            }
            const template = extractPropertyTemplate(objectType, templateName, templateNode);
            let templatesByName = templates.get(objectType);
            if (!templatesByName) {
                templatesByName = new Map();
                templates.set(objectType, templatesByName);
            }
            templatesByName.set(templateName, template);
        }
    }
    return templates;
}
export function getPropertyTemplate(templates, objectType, templateName) {
    const templatesByName = templates.get(objectType);
    if (!templatesByName) {
        return undefined;
    }
    if (templateName) {
        return templatesByName.get(templateName);
    }
    return templatesByName.values().next().value;
}
export function getTemplatePropertyValue(template, propertyName, valueIndex = 0) {
    return template?.properties.get(propertyName)?.values[valueIndex];
}
export function resolvePropertyValue(node, template, propertyName, valueIndex = 0) {
    return resolvePropertyValues(node, template, propertyName)?.[valueIndex];
}
export function resolveNumberProperty(node, template, propertyName, fallback) {
    return toNumber(resolvePropertyValue(node, template, propertyName)) ?? fallback;
}
export function resolveVector2Property(node, template, propertyName, fallback) {
    const values = resolvePropertyValues(node, template, propertyName);
    if (!values) {
        return fallback;
    }
    const x = toNumber(values[0]);
    const y = toNumber(values[1]);
    return x !== undefined && y !== undefined ? [x, y] : fallback;
}
export function resolveVector3Property(node, template, propertyName, fallback) {
    const values = resolvePropertyValues(node, template, propertyName);
    if (!values) {
        return fallback;
    }
    const x = toNumber(values[0]);
    const y = toNumber(values[1]);
    const z = toNumber(values[2]);
    return x !== undefined && y !== undefined && z !== undefined ? [x, y, z] : fallback;
}
export function resolvePropertyValues(node, template, propertyName) {
    return findLocalPropertyValues(node, propertyName) ?? template?.properties.get(propertyName)?.values;
}
function toNumber(value) {
    if (typeof value === "number") {
        return value;
    }
    return undefined;
}
function extractPropertyTemplate(objectType, templateName, templateNode) {
    const properties = new Map();
    const properties70 = findChildByName(templateNode, "Properties70");
    for (const propertyNode of properties70?.children ?? []) {
        if (propertyNode.name !== "P") {
            continue;
        }
        const property = extractPropertyNode(propertyNode);
        if (property) {
            properties.set(property.name, property);
        }
    }
    return { objectType, templateName, properties };
}
/**
 * Lists the properties declared directly on an object, from both the 7.x and 6.x property blocks.
 * @param node - Object node
 * @returns Property entries in declaration order (empty when the node has no property block)
 */
export function getPropertyEntries(node) {
    const out = [];
    if (!node) {
        return out;
    }
    for (const container of node.children) {
        if (container.name !== "Properties70" && container.name !== "Properties60") {
            continue;
        }
        for (const p of container.children) {
            if (p.name !== "P" && p.name !== "PS" && p.name !== "Property") {
                continue;
            }
            const name = getPropertyValue(p, 0);
            if (typeof name !== "string") {
                continue;
            }
            // The layout follows the entry node, not its container: `P` / `PS` carry name, type, label and flags,
            // `Property` carries name, type and flags. Transitional 6.x files write `P` entries inside Properties60.
            const hasLabel = p.name !== "Property";
            const type = getPropertyValue(p, 1);
            const flags = getPropertyValue(p, hasLabel ? 3 : 2);
            out.push({
                name,
                type: typeof type === "string" ? type : "",
                flags: typeof flags === "string" ? flags : "",
                values: p.properties.slice(hasLabel ? 4 : 3).map((property) => property.value),
            });
        }
    }
    return out;
}
/**
 * User-defined properties of an object: those flagged "U" (user) in the property flags. Every DCC exports custom
 * attributes this way (Maya extra attributes, 3ds Max user properties, Blender custom properties).
 * @param node - Object node
 * @returns Map of property name to value, or undefined when the object has none
 */
export function extractUserProperties(node) {
    const result = {};
    let any = false;
    for (const entry of getPropertyEntries(node)) {
        if (!entry.flags.includes("U")) {
            continue;
        }
        const value = userPropertyValue(entry.values, entry.type);
        if (value !== undefined) {
            result[entry.name] = value;
            any = true;
        }
    }
    return any ? result : undefined;
}
/** Property types whose extra values are limits or labels rather than components (value, min, max). */
const SCALAR_PROPERTY_TYPES = new Set([
    "Integer",
    "int",
    "Number",
    "double",
    "Float",
    "float",
    "Bool",
    "bool",
    "Enum",
    "enum",
    "Visibility",
    "Roll",
    "Weight",
    "Distance",
    "KTime",
    "Time",
]);
export function userPropertyValue(values, typeName) {
    if (values.length === 0) {
        return undefined;
    }
    const first = values[0];
    if (typeName !== undefined && SCALAR_PROPERTY_TYPES.has(typeName) && (typeof first === "number" || typeof first === "boolean")) {
        return typeof first === "boolean" ? first : first;
    }
    if (values.length === 1) {
        return typeof first === "string" || typeof first === "number" || typeof first === "boolean" ? first : undefined;
    }
    const numbers = [];
    for (const v of values) {
        if (typeof v === "number") {
            numbers.push(v);
        }
        else if (typeof v === "boolean") {
            numbers.push(v ? 1 : 0);
        }
        else {
            break;
        }
    }
    if (numbers.length === values.length) {
        return numbers;
    }
    return typeof first === "string" ? first : undefined;
}
function findLocalPropertyValues(node, propertyName) {
    const propertyContainers = [findChildByName(node, "Properties70"), findChildByName(node, "Properties60")].filter((child) => child !== undefined);
    // When a property is declared more than once the SDK keeps the last declaration.
    let found;
    for (const container of propertyContainers) {
        for (const propertyNode of container.children) {
            if (propertyNode.name !== "P" && propertyNode.name !== "Property") {
                continue;
            }
            if (getPropertyValue(propertyNode, 0) !== propertyName) {
                continue;
            }
            found = propertyNode.properties.slice(propertyNode.name === "Property" ? 3 : 4).map((property) => property.value);
        }
    }
    return found;
}
function extractPropertyNode(node) {
    const name = getPropertyValue(node, 0);
    if (!name) {
        return null;
    }
    return {
        name,
        propertyType: getPropertyValue(node, 1) ?? "",
        label: getPropertyValue(node, 2) ?? "",
        flags: getPropertyValue(node, 3) ?? "",
        values: node.properties.slice(4).map((property) => property.value),
    };
}
//# sourceMappingURL=propertyTemplates.js.map